<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { formatTime } from './composables/ispFormatters';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';

const store = useStore();
const { t } = useI18n();

const monitoring = computed(() => store.getters['ispReports/getMonitoring']);
const activeFilter = ref('all');
// GK-HUB: Removed lastRefreshAgo UI counter — LIVE badge is sufficient. Track internally for fallback polling only.
const lastRefreshAgo = ref(0);

// GK-HUB: Team filter for manager/admin (standardized — same pattern as IspFcr/IspPainel)
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const userRole = computed(() =>
  getUserRole(currentUser.value, currentAccountId.value)
);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(userRole.value)
);
const teams = computed(() => store.getters['teams/getTeams']);
const selectedTeamId = ref('');

// Fallback polling interval (30s) - only fires if WebSocket data stops arriving
const FALLBACK_INTERVAL = 30000;

const fetchData = () => {
  const params = {};
  // GK-HUB: Pass team_id instead of department_id (standardized)
  if (selectedTeamId.value) {
    params.teamId = selectedTeamId.value;
  }
  store.dispatch('ispReports/fetchMonitoring', params);
  lastRefreshAgo.value = 0;
};
// GK-HUB: end team filter setup

// Watch for WebSocket-pushed data updates and reset the counter
watch(
  () => monitoring.value.data,
  () => {
    lastRefreshAgo.value = 0;
  }
);

let refreshInterval = null;
let tickInterval = null;

onMounted(() => {
  // Initial fetch via HTTP (WebSocket data hasn't arrived yet)
  fetchData();
  // GK-HUB: Fetch teams for dropdown (standardized — same as IspFcr/IspPainel)
  if (showTeamFilter.value) store.dispatch('teams/get');
  // Fallback polling: if WebSocket stops, HTTP keeps the dashboard alive
  refreshInterval = setInterval(() => {
    if (lastRefreshAgo.value >= FALLBACK_INTERVAL / 1000 - 2) {
      fetchData();
    }
  }, FALLBACK_INTERVAL);
  // GK-HUB: Tick interval kept for fallback polling counter (no UI display)
  tickInterval = setInterval(() => {
    lastRefreshAgo.value += 1;
  }, 1000);
});

// GK-HUB: Re-fetch when team filter changes (standardized)
watch(selectedTeamId, () => {
  fetchData();
});

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
  if (tickInterval) clearInterval(tickInterval);
});

const monitoringError = computed(() => monitoring.value.error);
const retry = fetchData;

const kpis = computed(() => monitoring.value.data?.kpis || {});
const conversations = computed(
  () => monitoring.value.data?.conversations || []
);
const agents = computed(() => monitoring.value.data?.agents || []);
const alerts = computed(() => monitoring.value.data?.alerts || []);
// GK-HUB: Queue metrics per team (null when no queue-enabled teams)
const queueData = computed(() => monitoring.value.data?.queue || null);

const filteredConversations = computed(() => {
  if (activeFilter.value === 'all') return conversations.value;
  if (activeFilter.value === 'queue')
    return conversations.value.filter(c => c.status === 'queue');
  if (activeFilter.value === 'active')
    return conversations.value.filter(c => c.status === 'active');
  if (activeFilter.value === 'waiting')
    return conversations.value.filter(c => c.status === 'waiting_customer');
  return conversations.value;
});

const queueCount = computed(
  () => conversations.value.filter(c => c.status === 'queue').length
);
const activeCount = computed(
  () => conversations.value.filter(c => c.status === 'active').length
);
const waitingCount = computed(
  () => conversations.value.filter(c => c.status === 'waiting_customer').length
);

const onlineAgents = computed(() =>
  agents.value.filter(a => a.availability === 'online')
);

// GK-HUB: Count agents on break
const onBreakCount = computed(
  () =>
    kpis.value.agentes_em_pausa ||
    agents.value.filter(a => a.availability === 'on_break').length
);
// GK-HUB: end on break count

const connectionStatus = computed(() => {
  if (lastRefreshAgo.value >= 90) return 'disconnected';
  if (lastRefreshAgo.value >= 45) return 'reconnecting';
  return 'connected';
});

const formatAgo = seconds => {
  if (!seconds && seconds !== 0) return '--';
  if (seconds < 60) return `${seconds}s atras`;
  const m = Math.floor(seconds / 60);
  return `${m}m atras`;
};

const formatEsperaMaior = seconds => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  return `${m}m`;
};

const durationClass = seconds => {
  if (!seconds) return '';
  const m = seconds / 60;
  if (m >= 10) return 'text-n-ruby-9 font-semibold';
  if (m >= 5) return 'text-n-amber-9';
  return '';
};

const kpiEsperaClass = computed(() => {
  const s = kpis.value.espera_maior_seconds || 0;
  if (s >= 600) return 'border-n-ruby-9/40 [&_.kpi-val]:text-n-ruby-9';
  if (s >= 300) return 'border-n-amber-9/30 [&_.kpi-val]:text-n-amber-9';
  return '';
});

const kpiFilaClass = computed(() => {
  const n = kpis.value.na_fila || 0;
  if (n >= 10) return 'border-n-ruby-9/40 [&_.kpi-val]:text-n-ruby-9';
  if (n >= 3) return 'border-n-amber-9/30 [&_.kpi-val]:text-n-amber-9';
  return '';
});

const channelClasses = type => {
  const map = {
    whatsapp: 'bg-green-500/10 text-green-600',
    widget: 'bg-n-blue-9/10 text-n-blue-11',
    instagram: 'bg-pink-500/10 text-pink-600',
    facebook: 'bg-blue-600/10 text-blue-600',
    email: 'bg-n-blue-9/10 text-n-blue-11',
    telegram: 'bg-cyan-500/10 text-cyan-600',
    api: 'bg-n-blue-9/10 text-n-blue-11',
    phone: 'bg-n-teal-9/10 text-n-teal-11',
  };
  return map[type] || 'bg-n-slate-9/10 text-n-slate-11';
};

const channelLabel = type => {
  const map = {
    whatsapp: 'WhatsApp',
    widget: 'Widget',
    instagram: 'Instagram',
    facebook: 'Facebook',
    email: 'Email',
    telegram: 'Telegram',
    api: 'API',
    sms: 'SMS',
    phone: 'Telefonia',
  };
  return map[type] || type;
};

const statusBadge = status => {
  if (status === 'queue') return 'bg-n-amber-9/[.12] text-n-amber-9';
  if (status === 'active') return 'bg-n-teal-9/[.12] text-n-teal-9';
  if (status === 'waiting_customer') return 'bg-n-blue-9/[.12] text-n-blue-9';
  return 'bg-n-amber-9/[.12] text-n-amber-9';
};

const statusDotClass = status => {
  if (status === 'queue') return 'bg-n-amber-9 animate-live-pulse';
  if (status === 'active') return 'bg-n-teal-9';
  if (status === 'waiting_customer') return 'bg-n-blue-9';
  return 'bg-n-amber-9 animate-live-pulse';
};

const statusLabel = status => {
  if (status === 'queue') return t('ISP_REPORTS.MONITORING.STATUS_NA_FILA');
  if (status === 'active') return t('ISP_REPORTS.MONITORING.STATUS_ATENDENDO');
  if (status === 'waiting_customer')
    return t('ISP_REPORTS.MONITORING.STATUS_AG_CLIENTE');
  return status;
};

const isQueueCritical = conv => {
  return conv.status === 'queue' && conv.duration_seconds >= 600;
};

// GK-HUB: Added on_break status with pulsing amber dot
const agentDotClass = availability => {
  if (availability === 'online')
    return 'bg-n-teal-9 shadow-[0_0_6px_rgba(93,173,226,0.4)]';
  if (availability === 'busy')
    return 'bg-n-amber-9 shadow-[0_0_6px_rgba(245,158,11,0.3)]';
  if (availability === 'on_break')
    return 'bg-n-amber-9 shadow-[0_0_6px_rgba(245,158,11,0.3)] animate-live-pulse';
  return 'bg-n-slate-8 opacity-50';
};

// GK-HUB: Format break duration for display
const formatBreakDuration = seconds => {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
};
// GK-HUB: end break helpers

const agentConvClass = (count, maxCapacity) => {
  const max = maxCapacity || 8;
  const pct = (count / max) * 100;
  if (pct >= 90) return 'bg-n-ruby-9/10 text-n-ruby-9';
  if (pct >= 65) return 'bg-n-amber-9/10 text-n-amber-9';
  return 'bg-n-teal-9/10 text-n-teal-9';
};

// GK-HUB: Dynamic load percent using max_capacity from queue config (fallback 8)
const agentLoadPercent = (count, maxCapacity) => {
  const max = maxCapacity || 8;
  return Math.min((count / max) * 100, 100);
};

const agentLoadFillClass = (count, maxCapacity) => {
  const max = maxCapacity || 8;
  const pct = (count / max) * 100;
  if (pct >= 90) return 'bg-n-ruby-9';
  if (pct >= 65) return 'bg-n-amber-9';
  return 'bg-n-teal-9';
};

// GK-HUB: Priority badge helpers for conversation table
const priorityBadgeClass = priority => {
  const map = {
    urgent: 'bg-n-ruby-9/[.12] text-n-ruby-9',
    high: 'bg-n-amber-9/[.12] text-n-amber-9',
    medium: 'bg-n-blue-9/[.12] text-n-blue-9',
    low: 'bg-n-slate-9/[.12] text-n-slate-11',
  };
  return map[priority] || '';
};

const priorityLabel = priority => {
  const map = {
    urgent: t('ISP_REPORTS.MONITORING.PRIORIDADE_URGENTE'),
    high: t('ISP_REPORTS.MONITORING.PRIORIDADE_ALTA'),
    medium: t('ISP_REPORTS.MONITORING.PRIORIDADE_MEDIA'),
    low: t('ISP_REPORTS.MONITORING.PRIORIDADE_BAIXA'),
  };
  return map[priority] || '';
};

// GK-HUB: Queue fill percentage for team cards
const queueFillClass = (naFila, capacidade) => {
  if (capacidade <= 0) return 'bg-n-ruby-9';
  const ratio = naFila / capacidade;
  if (ratio >= 1) return 'bg-n-ruby-9';
  if (ratio >= 0.6) return 'bg-n-amber-9';
  return 'bg-n-teal-9';
};
// GK-HUB: end queue/priority helpers

const alertBorderClass = type => {
  if (type === 'critical') return 'border-l-n-ruby-9';
  if (type === 'warning') return 'border-l-n-amber-9';
  return 'border-l-n-blue-9';
};

const alertIcon = alert => {
  if (alert.icon === 'queue') return '\u26A0';
  if (alert.icon === 'agent') return '\uD83D\uDC64';
  return '\u23F1';
};

const formatAlertTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
</script>

<template>
  <div class="p-6">
    <!-- GK-HUB: Standardized header (same pattern as IspFcr/IspConversas/IspPainel) -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.MONITORING.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.MONITORING.SUBTITLE') }}
      </p>
    </div>

    <!-- GK-HUB: Standardized filter bar (isp-select-wrap pattern + connection badges) -->
    <div class="flex items-center gap-3 mb-5 flex-wrap">
      <!-- GK-HUB: Team filter (admin/manager only) — isp-select-wrap pattern -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select v-model="selectedTeamId" class="isp-select">
          <option value="">
            {{ t('ISP_REPORTS.PAINEL.TODOS_TIMES') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <!-- Connection status badges (moved from title to filter bar) -->
      <div class="ml-auto flex items-center gap-2">
        <span
          v-if="connectionStatus === 'connected'"
          class="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-n-teal-9/[.12] text-n-teal-11 px-2.5 py-0.5 rounded-full"
        >
          <span
            class="w-1.5 h-1.5 rounded-full bg-n-teal-9 animate-live-pulse"
          />
          {{ t('ISP_REPORTS.MONITORING.LIVE') }}
        </span>
        <span
          v-else-if="connectionStatus === 'reconnecting'"
          class="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-n-amber-9/[.12] text-n-amber-9 px-2.5 py-0.5 rounded-full"
        >
          <span
            class="w-1.5 h-1.5 rounded-full bg-n-amber-9 animate-live-pulse"
          />
          {{ t('ISP_REPORTS.MONITORING.RECONNECTING') }}
        </span>
        <span
          v-else
          class="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-n-ruby-9/[.12] text-n-ruby-9 px-2.5 py-0.5 rounded-full"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-n-ruby-9" />
          {{ t('ISP_REPORTS.MONITORING.DISCONNECTED') }}
        </span>
      </div>
    </div>
    <!-- GK-HUB: end standardized header/filters -->

    <!-- Loading (only when no data yet) -->
    <woot-loading-state
      v-if="monitoring.isFetching && !monitoring.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error (only when no data yet) -->
    <div
      v-else-if="monitoringError && !monitoring.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="monitoring.data">
      <!-- KPI Row -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] p-3.5 text-center transition-colors hover:border-n-strong"
        >
          <div
            class="text-[10px] font-semibold uppercase tracking-widest text-n-slate-8 mb-1.5"
          >
            {{ t('ISP_REPORTS.MONITORING.EM_ATENDIMENTO') }}
          </div>
          <div
            class="kpi-val text-[26px] font-bold leading-tight text-n-teal-9"
          >
            {{ kpis.em_atendimento || 0 }}
          </div>
          <div class="text-[11px] text-n-slate-8 mt-0.5">
            {{ t('ISP_REPORTS.MONITORING.CONVERSAS_ATIVAS') }}
          </div>
        </div>

        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] p-3.5 text-center transition-colors hover:border-n-strong"
          :class="kpiFilaClass"
        >
          <div
            class="text-[10px] font-semibold uppercase tracking-widest text-n-slate-8 mb-1.5"
          >
            {{ t('ISP_REPORTS.MONITORING.NA_FILA') }}
          </div>
          <div
            class="kpi-val text-[26px] font-bold leading-tight text-n-slate-12"
          >
            {{ kpis.na_fila || 0 }}
          </div>
          <div class="text-[11px] text-n-slate-8 mt-0.5">
            {{ t('ISP_REPORTS.MONITORING.AGUARDANDO_AGENTE') }}
          </div>
        </div>

        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] p-3.5 text-center transition-colors hover:border-n-strong"
          :class="kpiEsperaClass"
        >
          <div
            class="text-[10px] font-semibold uppercase tracking-widest text-n-slate-8 mb-1.5"
          >
            {{ t('ISP_REPORTS.MONITORING.ESPERA_MAIOR') }}
          </div>
          <div
            class="kpi-val text-[26px] font-bold leading-tight text-n-slate-12"
          >
            {{ formatEsperaMaior(kpis.espera_maior_seconds) }}
          </div>
          <div class="text-[11px] text-n-slate-8 mt-0.5">
            {{ t('ISP_REPORTS.MONITORING.TEMPO_MAX_FILA') }}
          </div>
        </div>

        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] p-3.5 text-center transition-colors hover:border-n-strong"
        >
          <div
            class="text-[10px] font-semibold uppercase tracking-widest text-n-slate-8 mb-1.5"
          >
            {{ t('ISP_REPORTS.MONITORING.AGENTES_ONLINE') }}
          </div>
          <div
            class="kpi-val text-[26px] font-bold leading-tight text-n-teal-9"
          >
            {{ kpis.agentes_online || 0
            }}<span class="text-sm text-n-slate-8 font-normal"
              >&nbsp;/ {{ kpis.agentes_total || 0 }}</span
            >
          </div>
          <!-- GK-HUB: Show on-break count next to available agents -->
          <div class="text-[11px] text-n-slate-8 mt-0.5">
            {{ t('ISP_REPORTS.MONITORING.DISPONIVEIS') }}
            <span v-if="onBreakCount > 0" class="text-n-amber-9 font-medium">
              &middot; {{ onBreakCount }} {{ t('AGENT_BREAK.EM_PAUSA') }}
            </span>
          </div>
          <!-- GK-HUB: end on-break KPI -->
        </div>

        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] p-3.5 text-center transition-colors hover:border-n-strong"
        >
          <div
            class="text-[10px] font-semibold uppercase tracking-widest text-n-slate-8 mb-1.5"
          >
            {{ t('ISP_REPORTS.MONITORING.OCUPACAO_MEDIA') }}
          </div>
          <div
            class="kpi-val text-[26px] font-bold leading-tight text-n-slate-12"
          >
            {{ kpis.ocupacao_media || 0 }}
          </div>
          <div class="text-[11px] text-n-slate-8 mt-0.5">
            {{ t('ISP_REPORTS.MONITORING.CONV_POR_AGENTE') }}
          </div>
        </div>
      </div>

      <!-- GK-HUB: Queue Overview section — mostra todos os times da account -->
      <div
        v-if="queueData"
        class="bg-n-solid-2 border border-n-weak rounded-[10px] p-4 mb-5"
      >
        <div class="flex items-center justify-between mb-3">
          <div
            class="text-[13px] font-semibold text-n-slate-12 flex items-center gap-2"
          >
            {{ t('ISP_REPORTS.MONITORING.FILA_TITULO') }}
            <span
              class="text-[10px] font-medium text-n-slate-8 bg-n-solid-3 px-2 py-0.5 rounded-full"
            >
              {{ queueData.teams.length }}
              {{ queueData.teams.length === 1 ? 'time' : 'times' }}
            </span>
          </div>
          <div class="flex items-center gap-4 text-[11px] text-n-slate-10">
            <span>
              {{ t('ISP_REPORTS.MONITORING.ESPERA_MEDIA') }}:
              <strong class="text-n-slate-12">{{
                formatEsperaMaior(queueData.tempo_medio_espera_seconds)
              }}</strong>
            </span>
            <span>
              {{ t('ISP_REPORTS.MONITORING.CAPACIDADE_LIVRE') }}:
              <strong class="text-n-teal-11"
                >{{ queueData.capacidade_livre }}
                {{ t('ISP_REPORTS.MONITORING.VAGAS') }}</strong
              >
            </span>
          </div>
        </div>

        <!-- GK-HUB: max-h for ~2 rows of cards + scroll if more teams -->
        <div
          class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[220px] overflow-y-auto isp-scroll-thin"
        >
          <div
            v-for="team in queueData.teams"
            :key="team.id"
            class="bg-n-solid-3 border border-n-weak/50 rounded-lg p-3 transition-colors hover:border-n-strong"
          >
            <div
              class="text-[12px] font-semibold text-n-slate-12 mb-2 truncate"
              :title="team.name"
            >
              {{ team.name }}
            </div>
            <div class="flex items-baseline gap-1.5 mb-1">
              <span
                class="text-lg font-bold leading-tight"
                :class="
                  team.na_fila >= 10
                    ? 'text-n-ruby-9'
                    : team.na_fila >= 3
                      ? 'text-n-amber-9'
                      : 'text-n-slate-12'
                "
              >
                {{ team.na_fila }}
              </span>
              <span class="text-[10px] text-n-slate-8">{{
                t('ISP_REPORTS.MONITORING.NA_FILA_LABEL')
              }}</span>
            </div>
            <!-- Capacity mini bar -->
            <div class="h-[3px] bg-n-solid-2 rounded overflow-hidden mb-2">
              <div
                class="h-full rounded transition-all duration-300"
                :class="
                  queueFillClass(
                    team.na_fila,
                    team.capacidade_livre + team.na_fila
                  )
                "
                :style="{
                  width: `${team.capacidade_livre + team.na_fila > 0 ? Math.min((team.na_fila / (team.capacidade_livre + team.na_fila)) * 100, 100) : 0}%`,
                }"
              />
            </div>
            <div
              class="flex items-center justify-between text-[10px] text-n-slate-8"
            >
              <span
                >{{ team.capacidade_livre }}
                {{ t('ISP_REPORTS.MONITORING.VAGAS') }}</span
              >
              <span
                >{{ team.agentes_disponiveis }}
                {{ t('ISP_REPORTS.MONITORING.AG_DISPONIVEIS') }}</span
              >
            </div>
          </div>
        </div>
      </div>
      <!-- GK-HUB: end Queue Overview section -->

      <!-- Content Grid: Table + Agents -->
      <div class="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-4 mb-5">
        <!-- Conversations Panel -->
        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
        >
          <div
            class="px-4 py-3 flex items-center justify-between border-b border-n-weak bg-n-solid-3"
          >
            <div class="text-[13px] font-semibold text-n-slate-12">
              {{ t('ISP_REPORTS.MONITORING.CONVERSAS_TITULO') }}
            </div>
            <span
              class="text-[11px] text-n-slate-8 bg-n-solid-2 px-2 py-0.5 rounded-full"
            >
              {{ conversations.length }}
              {{ t('ISP_REPORTS.MONITORING.CONVERSAS_LABEL') }}
            </span>
          </div>

          <div class="px-4 py-2.5 flex gap-2 border-b border-n-weak flex-wrap">
            <button
              class="text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all font-[inherit] cursor-pointer"
              :class="
                activeFilter === 'all'
                  ? 'bg-n-teal-9/10 border-n-teal-9 text-n-teal-11 font-semibold'
                  : 'border-n-weak text-n-slate-10 hover:border-n-strong hover:text-n-slate-12'
              "
              @click="activeFilter = 'all'"
            >
              {{ t('ISP_REPORTS.MONITORING.FILTER_TODAS') }}
            </button>
            <button
              class="text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all font-[inherit] cursor-pointer"
              :class="
                activeFilter === 'queue'
                  ? 'bg-n-teal-9/10 border-n-teal-9 text-n-teal-11 font-semibold'
                  : 'border-n-weak text-n-slate-10 hover:border-n-strong hover:text-n-slate-12'
              "
              @click="activeFilter = 'queue'"
            >
              {{ t('ISP_REPORTS.MONITORING.FILTER_NA_FILA') }}
              ({{ queueCount }})
            </button>
            <button
              class="text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all font-[inherit] cursor-pointer"
              :class="
                activeFilter === 'active'
                  ? 'bg-n-teal-9/10 border-n-teal-9 text-n-teal-11 font-semibold'
                  : 'border-n-weak text-n-slate-10 hover:border-n-strong hover:text-n-slate-12'
              "
              @click="activeFilter = 'active'"
            >
              {{ t('ISP_REPORTS.MONITORING.FILTER_ATENDENDO') }}
              ({{ activeCount }})
            </button>
            <button
              class="text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all font-[inherit] cursor-pointer"
              :class="
                activeFilter === 'waiting'
                  ? 'bg-n-teal-9/10 border-n-teal-9 text-n-teal-11 font-semibold'
                  : 'border-n-weak text-n-slate-10 hover:border-n-strong hover:text-n-slate-12'
              "
              @click="activeFilter = 'waiting'"
            >
              {{ t('ISP_REPORTS.MONITORING.FILTER_AGUARDANDO') }}
              ({{ waitingCount }})
            </button>
          </div>

          <!-- GK-HUB: max-h ~480px + scroll + sticky thead for conversations -->
          <div
            class="overflow-x-auto overflow-y-auto max-h-[480px] isp-scroll-thin"
          >
            <table class="w-full border-collapse min-w-[780px]">
              <thead class="sticky top-0 z-[1] bg-n-solid-2">
                <tr>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.CONTATO') }}
                  </th>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.AGENTE') }}
                  </th>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.CANAL') }}
                  </th>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.MOTIVO') }}
                  </th>
                  <!-- GK-HUB: Priority column header -->
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.PRIORIDADE') }}
                  </th>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.DURACAO') }}
                  </th>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.ULTIMA_MSG') }}
                  </th>
                  <th
                    class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 px-3 py-2 text-left border-b border-n-weak"
                  >
                    {{ t('ISP_REPORTS.MONITORING.STATUS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="conv in filteredConversations"
                  :key="conv.id"
                  class="transition-colors hover:bg-n-alpha-2"
                >
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50 last:[&_tr]:border-b-0"
                  >
                    <div class="flex items-center gap-2">
                      <div
                        class="w-7 h-7 rounded-full bg-n-solid-3 flex items-center justify-center text-[10px] font-semibold text-n-slate-10 shrink-0"
                      >
                        {{ conv.contact_initials || '?' }}
                      </div>
                      <div>
                        <div class="font-medium text-n-slate-12 text-xs">
                          {{ conv.contact_name }}
                        </div>
                        <div
                          v-if="conv.contact_phone"
                          class="text-[10px] text-n-slate-8"
                        >
                          {{ conv.contact_phone }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span v-if="conv.agent_name" class="text-xs">
                      {{ conv.agent_name }}
                    </span>
                    <span v-else class="text-n-amber-9 italic text-[11px]">
                      {{ t('ISP_REPORTS.MONITORING.SEM_AGENTE') }}
                    </span>
                  </td>
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span
                      class="text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      :class="channelClasses(conv.channel_type)"
                    >
                      <!-- Channel icons -->
                      <svg
                        v-if="conv.channel_type === 'whatsapp'"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
                        />
                        <path
                          d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.616l4.573-1.467A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.239 0-4.308-.724-5.994-1.951l-.418-.312-2.73.876.892-2.657-.343-.545A9.935 9.935 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"
                        />
                      </svg>
                      <svg
                        v-else-if="
                          conv.channel_type === 'widget' ||
                          conv.channel_type === 'api'
                        "
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                        />
                      </svg>
                      <svg
                        v-else-if="conv.channel_type === 'instagram'"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="2" y="2" width="20" height="20" rx="5" />
                        <circle cx="12" cy="12" r="5" />
                      </svg>
                      <svg
                        v-else-if="conv.channel_type === 'email'"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <path d="M22 4L12 13 2 4" />
                      </svg>
                      <svg
                        v-else-if="conv.channel_type === 'telegram'"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"
                        />
                      </svg>
                      <svg
                        v-else-if="conv.channel_type === 'phone'"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"
                        />
                      </svg>
                      {{ channelLabel(conv.channel_type) }}
                    </span>
                  </td>
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span
                      v-if="conv.motivo"
                      class="text-[10px] font-medium px-2 py-0.5 rounded bg-n-solid-3 text-n-slate-11"
                    >
                      {{ conv.motivo }}
                    </span>
                    <span v-else class="text-[11px] italic text-n-slate-8">
                      {{ t('ISP_REPORTS.MONITORING.SEM_MOTIVO') }}
                    </span>
                  </td>
                  <!-- GK-HUB: Priority badge cell -->
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span
                      v-if="conv.priority"
                      class="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                      :class="priorityBadgeClass(conv.priority)"
                    >
                      {{ priorityLabel(conv.priority) }}
                    </span>
                  </td>
                  <!-- GK-HUB: end priority cell -->
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span
                      class="font-mono text-[11px]"
                      :class="durationClass(conv.duration_seconds)"
                    >
                      {{ formatTime(conv.duration_seconds) }}
                    </span>
                  </td>
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span class="font-mono text-[11px]">
                      {{ formatAgo(conv.last_message_seconds_ago) }}
                    </span>
                  </td>
                  <td
                    class="text-xs text-n-slate-12 px-3 py-2.5 align-middle border-b border-n-weak/50"
                  >
                    <span
                      class="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap"
                      :class="[
                        statusBadge(conv.status),
                        {
                          'bg-n-ruby-9/[.12] text-n-ruby-9':
                            isQueueCritical(conv),
                        },
                      ]"
                    >
                      <span
                        class="w-[5px] h-[5px] rounded-full"
                        :class="[
                          statusDotClass(conv.status),
                          {
                            'bg-n-ruby-9 animate-live-pulse':
                              isQueueCritical(conv),
                          },
                        ]"
                      />
                      {{ statusLabel(conv.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-if="!filteredConversations.length"
              class="text-center py-8 text-n-slate-9 text-sm"
            >
              {{ t('ISP_REPORTS.NO_DATA') }}
            </div>
          </div>
        </div>

        <!-- Agents Panel -->
        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
        >
          <div
            class="px-4 py-3 flex items-center justify-between border-b border-n-weak bg-n-solid-3"
          >
            <div class="text-[13px] font-semibold text-n-slate-12">
              {{ t('ISP_REPORTS.MONITORING.AGENTES') }}
            </div>
            <!-- GK-HUB: Show on-break count in agents panel header -->
            <span class="text-[11px] text-n-teal-9 font-medium">
              {{ onlineAgents.length }}
              {{ t('ISP_REPORTS.MONITORING.ONLINE') }}
              <span v-if="onBreakCount > 0" class="text-n-amber-9 ml-1">
                &middot; {{ onBreakCount }} {{ t('AGENT_BREAK.EM_PAUSA') }}
              </span>
            </span>
            <!-- GK-HUB: end agents panel header -->
          </div>

          <div class="max-h-[500px] overflow-y-auto">
            <div
              v-for="agent in agents"
              :key="agent.id"
              class="px-4 py-3 border-b border-n-weak/50 last:border-b-0 transition-colors hover:bg-n-alpha-2"
            >
              <div class="flex items-center gap-2 mb-1.5">
                <span
                  class="w-2 h-2 rounded-full shrink-0"
                  :class="agentDotClass(agent.availability)"
                />
                <span
                  class="text-[13px] font-medium flex-1"
                  :class="
                    agent.availability === 'offline'
                      ? 'text-n-slate-8'
                      : agent.availability === 'on_break'
                        ? 'text-n-amber-11'
                        : 'text-n-slate-12'
                  "
                >
                  {{ agent.name }}
                </span>
                <!-- GK-HUB: Show break badge for on_break agents -->
                <template v-if="agent.availability === 'on_break'">
                  <span
                    class="text-[10px] font-medium px-2 py-0.5 rounded-full bg-n-amber-9/[.12] text-n-amber-9 whitespace-nowrap"
                  >
                    {{ agent.break_reason || t('AGENT_BREAK.ON_BREAK') }}
                    <template v-if="agent.break_duration_seconds">
                      &middot;
                      {{ formatBreakDuration(agent.break_duration_seconds) }}
                    </template>
                  </span>
                </template>
                <!-- GK-HUB: end break badge -->
                <template v-else-if="agent.availability !== 'offline'">
                  <span
                    class="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    :class="
                      agentConvClass(
                        agent.conversations_count,
                        agent.max_capacity
                      )
                    "
                  >
                    <!-- GK-HUB: Show X/Y capacity when max_capacity is available -->
                    {{ agent.conversations_count
                    }}<template v-if="agent.max_capacity"
                      >/{{ agent.max_capacity }}</template
                    >
                  </span>
                </template>
                <span v-else class="text-[10px] text-n-slate-8">
                  {{ t('ISP_REPORTS.MONITORING.OFFLINE') }}
                </span>
              </div>

              <!-- GK-HUB: Hide load bar for on_break agents -->
              <template
                v-if="
                  agent.availability !== 'offline' &&
                  agent.availability !== 'on_break'
                "
              >
                <!-- GK-HUB: Dynamic capacity bar using max_capacity from queue config -->
                <div
                  class="h-[3px] bg-n-solid-3 rounded mt-1.5 overflow-hidden"
                >
                  <div
                    class="h-full rounded transition-all duration-300"
                    :class="
                      agentLoadFillClass(
                        agent.conversations_count,
                        agent.max_capacity
                      )
                    "
                    :style="{
                      width: `${agentLoadPercent(agent.conversations_count, agent.max_capacity)}%`,
                    }"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Alerts Section -->
      <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-3.5 mt-4">
        <div
          class="text-xs font-semibold text-n-slate-12 mb-2.5 flex items-center gap-2"
        >
          {{ t('ISP_REPORTS.MONITORING.ALERTAS') }}
          <span
            v-if="alerts.length"
            class="text-[10px] font-semibold bg-n-ruby-9/15 text-n-ruby-9 px-1.5 py-0.5 rounded-full"
          >
            {{ alerts.length }}
          </span>
        </div>

        <!-- GK-HUB: max-h ~200px + scroll for alerts (~5 visible) -->
        <div
          v-if="alerts.length"
          class="space-y-1.5 max-h-[200px] overflow-y-auto isp-scroll-thin"
        >
          <div
            v-for="(alert, idx) in alerts"
            :key="idx"
            class="flex items-center gap-2.5 px-3 py-2 bg-n-solid-3 rounded border-l-[3px] transition-colors hover:bg-n-alpha-2"
            :class="alertBorderClass(alert.type)"
          >
            <span class="text-sm shrink-0">{{ alertIcon(alert) }}</span>
            <div class="flex-1 text-[13px] text-n-slate-10">
              <strong class="text-n-slate-12 font-medium">{{
                alert.message
              }}</strong>
              <span v-if="alert.detail"> &mdash; {{ alert.detail }}</span>
            </div>
            <span
              class="text-[10px] text-n-slate-8 font-mono whitespace-nowrap"
            >
              {{ formatAlertTime(alert.time) }}
            </span>
          </div>
        </div>

        <div v-else class="text-sm text-n-slate-9 py-2">
          {{ t('ISP_REPORTS.MONITORING.SEM_ALERTAS') }}
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <div
      v-if="!monitoring.isFetching && !monitoringError && !monitoring.data"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_MONITORING_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_MONITORING_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
@keyframes livePulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
.animate-live-pulse {
  animation: livePulse 2s infinite;
}

/* GK-HUB: Thin scrollbar for constrained sections (queue, conversations, alerts) */
.isp-scroll-thin {
  scrollbar-width: thin;
  scrollbar-color: #2a3650 transparent;
}
.isp-scroll-thin::-webkit-scrollbar {
  width: 4px;
}
.isp-scroll-thin::-webkit-scrollbar-track {
  background: transparent;
}
.isp-scroll-thin::-webkit-scrollbar-thumb {
  background: #2a3650;
  border-radius: 9999px;
}

/* GK-HUB: Standardized select wrapper (same pattern as IspFcr/IspMotivos/IspPainel) */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}
</style>

<!-- GK-HUB: Dark mode overrides for standardized selects (unscoped for body.dark targeting) -->
<style>
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
</style>
